import { useQuery, useMutation } from "react-query";
import {
  acceptUserInvitations,
  authorize,
  forgotPassword,
  getUserInvitations,
  getUserMembership,
  login,
  rejectUserInvitations,
  resetPasswordFromGuid,
  updateUser,
} from "../services/user";
import { queryClient } from "../App";
import { InvitationType, Member, UserType } from "../types";
import { ORGANIZATION_MEMBERSHIPS } from "../util/constants";

export const useAuthorize = (token?: string | null) => {
  return useQuery("user", async () => await authorize(token), {
    refetchOnMount: false,
    retry: false,
    enabled: !!token,
  });
};

export const useLogin = () => {
  return useMutation("token", login);
};

export const useGetUserMembership = (enabled?: boolean) => {
  const additionalParams: Record<string, string | number | boolean> = {};
  if (enabled !== undefined) {
    additionalParams.enabled = enabled;
  }

  return useQuery("user-memberships", () => getUserMembership(), {
    refetchOnMount: false,
    retry: false,
    ...additionalParams,
  });
};

export const useGetUserInvitations = (userId: number | undefined) => {
  return useQuery(
    "user-invitations",
    () => getUserInvitations(userId as number),
    {
      enabled: !!userId,
      refetchOnMount: false,
      retry: false,
    }
  );
};

const onInvitationSuccess = (
  response: any,
  variables: { invitationId: number }
) => {
  queryClient.setQueryData(
    "user-invitations",
    (value: InvitationType[] | any) => {
      return value?.filter(
        (item: InvitationType) => item.id !== variables.invitationId
      );
    }
  );
};

export const useRejectInvitationMutation = () => {
  return useMutation(
    ({ invitationId }: { invitationId: number }): Promise<unknown | any> => {
      return rejectUserInvitations(invitationId);
    },
    {
      onSuccess: onInvitationSuccess,
      retry: false,
    }
  );
};

export const useAcceptInvitationMutation = () => {
  return useMutation(
    ({ invitationId }: { invitationId: number }): Promise<unknown | any> => {
      return acceptUserInvitations(invitationId);
    },
    {
      onSuccess: onInvitationSuccess,
      retry: false,
    }
  );
};

type ResponseUserSuccessType = {
  crypto_uid?: string;
  email: string;
  first_name: string;
  id: number;
  last_name: string;
  type?: string;
};

const onUpdateUserSuccess = (
  response: ResponseUserSuccessType,
  variables: { userId: number; organizationId: number }
) => {
  queryClient.setQueryData(
    [ORGANIZATION_MEMBERSHIPS, variables.organizationId],
    (data: Member[] | any) => {
      return data.map((item: Member) => {
        let newData = { ...item };
        if (item.user_id === variables.userId) {
          const obj = {
            user_email: response.email,
            user_first_name: response.first_name,
            user_last_name: response.last_name,
          };
          newData = { ...item, ...obj };
        }
        return newData;
      });
    }
  );
};

export const useOnUpdateUserMutation = () => {
  return useMutation(
    ({
      userId,
      userData,
    }: {
      userId: number;
      organizationId: number;
      userData: UserType;
    }): Promise<unknown | any> => {
      return updateUser(userId, userData);
    },
    {
      onSuccess: onUpdateUserSuccess,
      retry: false,
    }
  );
};

export const useForgotPassword = () => {
  return useMutation(
    ({ email }: { email: string }): Promise<unknown | any> => {
      return forgotPassword(email);
    },
    {
      retry: false,
    }
  );
};

export const useResetPasswordFromGuid = () => {
  return useMutation(
    ({
      userId,
      password,
      guid,
    }: {
      userId: number;
      password: string;
      guid: string;
    }): Promise<unknown | any> => {
      return resetPasswordFromGuid(userId, password, guid);
    },
    {
      retry: false,
    }
  );
};
